import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { CheckCircle, XCircle, Loader2 } from 'lucide-react'
import HelpPopover from '../../components/help/HelpPopover'

type Status = 'loading' | 'success' | 'error' | 'invalid'

export default function CohortInviteAccept() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<Status>('loading')
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')

  useEffect(() => {
    const token = searchParams.get('token') || ''
    if (!token) {
      setStatus('invalid')
      setMessage('No invitation token found. Please use the link from your email.')
      return
    }
    claimInvite(token)
  }, [searchParams])

  async function claimInvite(token: string) {
    const config = typeof window !== 'undefined' ? window.INNOVATEFLOW_CONFIG : null
    const baseUrl = config?.API_ENDPOINT ?? ''

    try {
      const resp = await fetch(`${baseUrl}/innovateflow/cohorts/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await resp.json()

      if (resp.ok) {
        setEmail(data.email ?? '')
        setStatus('success')
        setMessage('Your invitation has been accepted. Check your email for login instructions.')
      } else if (resp.status === 409) {
        setStatus('error')
        setMessage('This invitation has already been used. Please log in or request a new link.')
      } else if (resp.status === 401) {
        setStatus('invalid')
        setMessage('This invitation link is invalid or has expired. Please request a new one.')
      } else {
        setStatus('error')
        setMessage(data.error ?? 'Something went wrong. Please try again later.')
      }
    } catch {
      setStatus('error')
      setMessage('Could not connect to the server. Please check your connection and try again.')
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-[#E8EFF4] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-[rgba(12,74,110,0.12)] shadow-xl p-8 max-w-md w-full text-center">
        <div className="mb-4">
          <img src="/logo.svg" alt="InnovateFlow" className="h-10 mx-auto" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }} />
        </div>

        <div className="mb-6 flex items-center justify-center gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-iflow-600">Cohort invitation</span>
          <HelpPopover
            topic="cohort-invite"
            title="What is a cohort invitation?"
            body="A cohort is a group (e.g. an accelerator intake or class) added to InnovateFlow together by an administrator. Accepting this invite links your email to that cohort so you can sign in with the rest of your group."
          />
        </div>

        {status === 'loading' && (
          <>
            <Loader2 className="w-12 h-12 text-iflow-500 animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-iflow-900 mb-2">Accepting your invitation…</h1>
            <p className="text-sm text-slate-500">This will only take a moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-iflow-900 mb-2">Invitation accepted!</h1>
            <p className="text-sm text-slate-600 mb-1">{message}</p>
            {email && <p className="text-xs text-slate-400 mb-6">Sent to: {email}</p>}
            <button
              onClick={() => navigate('/login')}
              className="w-full py-2.5 px-4 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 transition-colors"
            >
              Go to login
            </button>
          </>
        )}

        {(status === 'error' || status === 'invalid') && (
          <>
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-iflow-900 mb-2">
              {status === 'invalid' ? 'Invalid invitation' : 'Something went wrong'}
            </h1>
            <p role="alert" className="text-sm text-slate-600 mb-6">{message}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-2.5 px-4 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 transition-colors"
            >
              Go to login
            </button>
            <button
              onClick={() => navigate('/')}
              className="mt-3 w-full py-2.5 px-4 text-iflow-700 text-sm font-medium rounded-lg hover:bg-iflow-50 transition-colors"
            >
              Back to home
            </button>
          </>
        )}
      </div>
    </div>
  )
}
